import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Unlock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { OpenAccessEmailForm } from './OpenAccessEmailForm';
import { ADMIN_LOGIN_PATH } from '../App';
import { appendUtm } from '../utils/utm';

const fmt = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
};

/** Site-wide announcement for the open-access window: every story is free
 *  to read until `grace.ends_at`, an account is still required, and the
 *  paywall re-arms automatically when the clock runs out. Dismissal is
 *  remembered per window so it never nags twice. */
const FreeAccessPopup = () => {
  const { grace, user, loading } = useAuth();
  const { pathname } = useLocation();
  const onAdmin = pathname.startsWith(ADMIN_LOGIN_PATH);
  const endsAt = grace?.ends_at || '';
  const [dismissed, setDismissed] = useState(true);
  const [left, setLeft] = useState(grace?.seconds_remaining || 0);

  useEffect(() => {
    if (!grace?.active || !endsAt) return;
    let seen = null;
    try { seen = localStorage.getItem('outlaw_free_window_seen'); } catch { /* ignore */ }
    setDismissed(seen === endsAt);
  }, [grace?.active, endsAt]);

  useEffect(() => {
    if (!grace?.active || !endsAt) return undefined;
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(endsAt).getTime() - Date.now()) / 1000));
      setLeft(secs);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [grace?.active, endsAt]);

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem('outlaw_free_window_seen', endsAt); } catch { /* ignore */ }
  };

  if (loading || onAdmin || !grace?.active || dismissed || left <= 0) return null;

  return (
    <div
      data-testid="free-access-popup"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-lg rounded-lg border-2 border-red-600/70 bg-gradient-to-b from-red-950/70 to-black shadow-[0_0_70px_rgba(220,38,38,0.45)] p-6 sm:p-8">
        <button
          type="button"
          onClick={close}
          data-testid="free-access-close"
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-[0.3em] mb-4">
          <Unlock className="h-3.5 w-3.5" />
          Paywall Down · {fmt(left)} Left
        </div>

        <h2
          className="text-white text-2xl sm:text-3xl font-black leading-tight mb-4"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Every story is free to read for the next{' '}
          <span className="text-red-400" data-testid="free-access-countdown">{fmt(left)}</span>.
        </h2>

        <p className="text-zinc-300 text-sm sm:text-[15px] leading-relaxed mb-3">
          We believe the information in today's articles needs to be read and scrutinised by the
          masses. So we've dropped the paywall on everything &mdash; every investigation, the Rumour
          File, the Gangland Map and the videos.
        </p>
        <p className="text-yellow-200/90 text-sm leading-relaxed mb-6">
          <strong className="text-white">Just drop your email in.</strong> No card, no password &mdash;
          it joins the Outlaw list and sets up your free membership so the stories open straight away.
          When the clock runs out, the paywall goes back up.
        </p>

        {user ? (
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/stories"
              onClick={close}
              data-testid="free-access-read-cta"
              className="inline-flex items-center justify-center px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em] transition-colors"
            >
              Start reading
            </Link>
            <button
              type="button"
              onClick={close}
              data-testid="free-access-dismiss"
              className="inline-flex items-center justify-center px-5 py-3 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-bold text-xs uppercase tracking-[0.25em]"
            >
              Stay on this page
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <OpenAccessEmailForm testid="free-access" onDone={close} />
            <Link
              to={appendUtm("/login")}
              onClick={close}
              data-testid="free-access-login-link"
              className="block text-center text-zinc-400 hover:text-white text-[11px] uppercase tracking-[0.25em] py-1 transition-colors"
            >
              Already have an account? Log in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeAccessPopup;
